import { Box, FormGroup, makeStyles } from "@material-ui/core";
import React, { FC, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useRecoilState } from "recoil";

import { CustomForm } from "@/components/Form";
import { FormErrorText } from "@/components/FormErrorText";
import { CustomInput } from "@/components/Input";
import { RegistrationHelpText } from "@/components/registration/RegistrationHelpText";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { useGetMasterPurposeOfUses } from "@/hooks/useGetMasterData";
import { RegistrationActions, RegistrationFlowLayout as RegistrationTemplate } from "@/layouts/RegistrationFlowLayout";
import theme from "@/theme";
import { UserRegistrationsAccoutFormState } from "@/view_models/atoms";

import { RegistrationReasonChoice } from "./RegistrationReasonChoice";

const useStyles = makeStyles(() => ({
  choices: { gap: theme.spacing(1) },
  // 1行の欄（CustomForm）の上下余白を消し、選択肢・入力欄の間隔を gap の 24px にそろえる
  formContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    "& .MuiFormControl-root": { margin: 0 },
  },
  body: {
    "& > :last-child": {
      marginTop: theme.spacing(3),
      [theme.breakpoints.up("md")]: { marginTop: theme.spacing(4) },
    },
  },
  requestHelp: { marginTop: theme.spacing(1) },
}));

// 「その他」の文字数上限。1行の欄に変える前の複数行欄（CustomInput の既定値）と同じ。
const OTHER_PURPOSE_MAX_LENGTH = 100;
const OTHER_REQUEST_PLACEHOLDER =
  "例1：初心者なので、メリットだけでなくリスク面もきちんと教えてほしいです。\n例2：立地は一都三県と大阪・福岡、築15年以内の物件があれば提案を希望します。";

interface RegisterReasonPageProps {
  totalSteps: number;
  currentStep: number;
  onNext: () => Promise<void>;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

const RegisterReasonPage: FC<RegisterReasonPageProps> = ({ totalSteps, currentStep, onBack, validate, onNext }) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext();
  const [formData] = useRecoilState(UserRegistrationsAccoutFormState);

  const { masterPurposeOfUses } = useGetMasterPurposeOfUses();

  const handleNext = async () => {
    const isValid = await validate();
    if (isValid) {
      await onNext();
    }
  };

  const selectedPurposeOfUseIds = useWatch<number[]>({
    name: "purpose_of_use_ids",
    control,
    defaultValue: control.getValues("purpose_of_use_ids") || [],
  });

  useEffect(() => {
    // formDataが存在する場合に初期値を設定
    // 廃止したライフプランを下書きから引き継いで、見えない選択として送信しない。
    control.setValue("purpose_of_use_ids", formData.purpose_of_use_ids?.filter((id) => id !== 8) ?? []);
    control.setValue("purpose_of_use_free_text", formData.purpose_of_use_free_text);
    control.setValue("other_request_text", formData.other_request_text);
  }, []);

  // アンケート用の関数に変更が必要
  const handlePurposeOfUseId = (checkedId) => {
    const { purpose_of_use_ids: selected_ids } = getValues();
    return selected_ids?.includes(checkedId)
      ? selected_ids?.filter((id) => id !== checkedId)
      : [...(selected_ids ?? []), checkedId];
  };

  return (
    <RegistrationTemplate
      phaseTags={<RegistrationPhases completed />}
      title={"プロパリーで\nやりたいことを教えてください"}
      description="あてはまるものをすべて選んでください"
      descriptionSize="small"
      bodyClassName={classes.body}
      totalSteps={totalSteps}
      currentStep={currentStep}
    >
      <Box className={classes.formContent}>
        <Controller
          name="purpose_of_use_ids"
          control={control}
          render={({ ...field }) => (
            <FormGroup className={classes.choices}>
              {masterPurposeOfUses.filter((reason) => reason.id !== 8).map((reason) => (
                <RegistrationReasonChoice
                  {...field}
                  key={reason.id}
                  label={reason.name}
                  error={!!errors.purpose_of_use_ids}
                  noticeTitle={[16, 17].includes(reason.id) ? "事務局から日程調整のご連絡をします" : undefined}
                  helperText={
                    reason.id === 16
                      ? "不動産投資の全体像や注意点、目的に沿った戦略をお伝えします。弊社は不動産会社ではないため、販売の営業は一切行っていません。"
                      : reason.id === 17
                        ? "購入・売却を検討中の物件について、その物件の良し悪しや今後の戦略を、不動産会社ではない弊社のフラットな視点からアドバイスします。"
                        : ""
                  }
                  onChange={() => field.onChange(handlePurposeOfUseId(reason.id))}
                  checked={field.value?.includes(reason.id) ?? false}
                />
              ))}
            </FormGroup>
          )}
        />
        {/* ID=6（その他）を選択したときの必須入力欄 */}
        {selectedPurposeOfUseIds.includes(6) && (
          <Controller
            name="purpose_of_use_free_text"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                maxLength={OTHER_PURPOSE_MAX_LENGTH}
                label="その他"
                error={!!errors.purpose_of_use_free_text}
                errorText={errors.purpose_of_use_free_text?.message}
                onChange={field.onChange}
              />
            )}
          />
        )}

        {/* 特定項目の選択時に表示する任意の追加入力欄 */}
        {selectedPurposeOfUseIds?.some((id) => [2, 3, 5, 9, 10, 11, 12].includes(id)) && (
          <Box>
            <Controller
              name="other_request_text"
              control={control}
              render={({ ...field }) => (
                <CustomInput
                  {...field}
                  label="ご要望・ご相談があれば、入力してください（任意）"
                  placeholder={OTHER_REQUEST_PLACEHOLDER}
                  maxLength={2000}
                  error={undefined}
                  onChange={field.onChange}
                />
              )}
            />
            <RegistrationHelpText className={classes.requestHelp}>
              記載いただくと、より正確なメッセージが届きます
            </RegistrationHelpText>
          </Box>
        )}
      </Box>
      {errors && errors.purpose_of_use_ids && <FormErrorText>{errors.purpose_of_use_ids.message}</FormErrorText>}
      <RegistrationActions onNext={handleNext} onBack={onBack} label="登録を完了する" />
    </RegistrationTemplate>
  );
};
export default RegisterReasonPage;
